import type { LucideIcon } from 'lucide-react'

import { cn } from '#/lib/utils'

// Section label typography — the design system's `section-label`: small
// sentence-case sans, muted, never uppercase or tracked. Shared by the
// PageHeader eyebrow, SectionLabel, labels that can't be a heading of their own
// (one inside a disclosure's toggle button, say) and headings below the h2 that
// SectionLabel draws, so they can't drift.
export const SECTION_LABEL_CLASS = 'text-label-md text-muted-foreground'

// The header row at the top of a column beside the rail — the workspace
// sub-nav's switcher, each split-view pane's title bar — so their rules run on
// as one line and their labels share a centre.
export const HEADER_ROW_CLASS =
  'flex h-header-row shrink-0 items-center border-b border-border'

// A question to ask, as a reader would phrase it: the serif's own italic in
// ink. Shared by an answer's follow-ups and the suggested questions under the
// question field, so the two read as one kind of thing.
export const QUESTION_TEXT_CLASS =
  'font-serif text-body-lg text-foreground italic'

/**
 * Serif display-title recipe shared by every PP Fragment heading (page titles,
 * card/dialog titles): serif, regular weight, plum. PP Fragment ships weight
 * 400 only, so the display scale's 700/-0.02em metadata is overridden here,
 * once.
 */
const DISPLAY_TITLE_SIZE = {
  xl: 'text-display-xl',
  lg: 'text-display-lg',
  md: 'text-display-md',
  sm: 'text-display-sm',
} as const

export function displayTitleClass(
  size: keyof typeof DISPLAY_TITLE_SIZE = 'lg',
) {
  return cn(
    'font-serif font-normal tracking-tight text-title',
    DISPLAY_TITLE_SIZE[size],
  )
}

/**
 * Page header: optional icon eyebrow, serif display title, muted
 * description, and an actions slot aligned to the title baseline.
 */
export function PageHeader({
  eyebrow,
  eyebrowIcon: EyebrowIcon,
  title,
  description,
  actions,
}: {
  eyebrow?: string
  eyebrowIcon?: LucideIcon
  title: string
  description?: React.ReactNode
  actions?: React.ReactNode
}) {
  return (
    <header className="flex flex-col gap-5">
      {eyebrow ? (
        <p className={cn('flex items-center gap-2', SECTION_LABEL_CLASS)}>
          {EyebrowIcon ? (
            <EyebrowIcon className="size-3.5" aria-hidden="true" />
          ) : null}
          {eyebrow}
        </p>
      ) : null}
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-4">
        <div className="flex flex-col gap-2">
          <h1 className={displayTitleClass()}>{title}</h1>
          {description ? (
            <p className="text-muted-foreground">{description}</p>
          ) : null}
        </div>
        {actions ? (
          <div className="flex items-center gap-3">{actions}</div>
        ) : null}
      </div>
    </header>
  )
}

/**
 * The small sentence-case section label — "All workspaces", "Suggested". Sans,
 * not mono: mono is reserved for codes and IDs.
 */
export function SectionLabel({
  className,
  ...props
}: React.ComponentProps<'h2'>) {
  return <h2 className={cn(SECTION_LABEL_CLASS, className)} {...props} />
}

/**
 * The heading that opens a section of a page — "Your workspaces", "Recent
 * questions": the display serif at card-title size, with an optional quiet
 * action (a "See all" link, say) on its baseline. Where a plain label is
 * enough, use SectionLabel.
 */
export function SectionHeader({
  title,
  action,
  id,
  className,
}: {
  title: string
  action?: React.ReactNode
  id?: string
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1',
        className,
      )}
    >
      <h2 id={id} className={displayTitleClass('sm')}>
        {title}
      </h2>
      {action}
    </div>
  )
}

// A quiet "see all" link for a SectionHeader's action: muted, plum on hover,
// its arrow leaning forward.
export const SECTION_LINK_CLASS =
  'group/more flex items-center gap-1 rounded-sm text-body-sm text-muted-foreground transition-colors hover:text-title focus-ring [&_svg]:size-3.5 [&_svg]:motion-safe:transition-transform hover:[&_svg]:translate-x-0.5'
